import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import ViewViewer from "./ViewViewer";

const mockAuthFetch = jest.fn();
const mockUseViewDataQuery = jest.fn();

jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("../hooks/useViewDataQuery", () => ({
  useWorkspaceObjectChangeQueryInvalidation: () => {},
  useViewQuery: () => ({
    data: { id: "view-1", name: "Employees", focus_table_id: "table-1" },
    isLoading: false,
    error: null,
  }),
  useViewDataQuery: (...args: unknown[]) => mockUseViewDataQuery(...args),
  useCreateRowMutation: () => ({ mutateAsync: jest.fn(), isPending: false }),
  useUpdateRowMutation: () => ({ mutateAsync: jest.fn(), isPending: false }),
  useDeleteRowMutation: () => ({ mutateAsync: jest.fn(), isPending: false }),
}));
jest.mock("../components/viewGrid", () => ({
  ViewGridNew: () => <div>grid</div>,
  ViewGridSkeleton: () => <div>loading</div>,
}));
jest.mock("../components/viewGrid/ViewGridErrorBoundary", () => ({
  __esModule: true,
  default: ({ children }: { children: React.ReactNode }) => children,
}));

it("focuses a passed row with the view focus table's actual primary key", async () => {
  mockAuthFetch.mockResolvedValue({ primary_key_column: "employee_key" });
  mockUseViewDataQuery.mockReturnValue({
    data: { rows: [], columns: [], totalCount: 0 },
    isLoading: false,
    error: null,
    refetch: jest.fn(),
  });
  const client = new QueryClient({
    defaultOptions: { queries: { retry: false } },
  });
  render(
    <QueryClientProvider client={client}>
      <MemoryRouter
        initialEntries={[
          "/workspace/workspace-1/myvault/views/view-1?row=emp-42",
        ]}
      >
        <Routes>
          <Route
            path="/workspace/:workspaceId/myvault/views/:id"
            element={<ViewViewer />}
          />
        </Routes>
      </MemoryRouter>
    </QueryClientProvider>,
  );
  await waitFor(() =>
    expect(mockUseViewDataQuery).toHaveBeenCalledWith(
      "view-1",
      expect.objectContaining({
        filters: [{ field: "employee_key", operator: "eq", value: "emp-42" }],
      }),
    ),
  );
  expect(mockAuthFetch).toHaveBeenCalledWith(
    "/api/workspace/myvault/schemas/tables/table-1",
    expect.any(String),
    expect.objectContaining({ headers: { "x-workspace-id": "workspace-1" } }),
  );
});

it("does not query unfiltered rows when the focus table has no primary key", async () => {
  mockAuthFetch.mockResolvedValue({ primary_key_column: null });
  mockUseViewDataQuery.mockClear();
  mockUseViewDataQuery.mockReturnValue({
    data: null,
    isLoading: false,
    error: null,
    refetch: jest.fn(),
  });
  const client = new QueryClient({
    defaultOptions: { queries: { retry: false } },
  });
  render(
    <QueryClientProvider client={client}>
      <MemoryRouter
        initialEntries={[
          "/workspace/workspace-1/myvault/views/view-1?row=emp-42",
        ]}
      >
        <Routes>
          <Route
            path="/workspace/:workspaceId/myvault/views/:id"
            element={<ViewViewer />}
          />
        </Routes>
      </MemoryRouter>
    </QueryClientProvider>,
  );
  expect(
    await screen.findByText(
      "This view has no primary key for the selected record.",
    ),
  ).toBeTruthy();
  expect(
    mockUseViewDataQuery.mock.calls.every(([viewId]) => viewId === null),
  ).toBe(true);
});
